import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';

export default function Safety() {
  return (
    <>
      <SEOHead
        title="Safety &amp; Security Information — LitBuy Spreadsheet"
        description="Your safety matters to us. Here is everything you need to know about browsing litbuyspreadsheets.one securely and responsibly, including HTTPS security, privacy practices, and safe browsing tips."
        canonicalPath="/safety"
        pageType="website"
        breadcrumbData={[
          { name: 'Home', path: '/' },
          { name: 'Safety', path: '/safety' },
        ]}
      />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-700">Safety</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              Safety &amp; Security Information
            </h1>
            <p className="mt-4 text-foreground-600 text-base leading-relaxed max-w-2xl">
              Your safety matters to us. Here is everything you need to know about browsing litbuyspreadsheets.one securely and responsibly.
            </p>
          </div>
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">HTTPS Security</h2>
            <p className="text-foreground-700 leading-relaxed">
              This website uses HTTPS encryption to protect data transmitted between your browser and our servers. All communications are encrypted, ensuring that third parties cannot intercept or tamper with the information you exchange with our site. You can verify the secure connection by looking for the padlock icon in your browser&apos;s address bar.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">User Privacy</h2>
            <p className="text-foreground-700 leading-relaxed mb-3">
              LitBuy Spreadsheet Resource Center respects your privacy. We do not collect, store, or process personal information beyond what is strictly necessary for the website to function. Our site does not use tracking cookies, analytics scripts, or any form of user profiling technology.
            </p>
            <p className="text-foreground-700 leading-relaxed">
              Any information you provide through contact forms or newsletter subscriptions is handled in accordance with our Privacy Policy. We never sell, rent, or share your personal data with third parties for marketing purposes.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Data Protection</h2>
            <p className="text-foreground-700 leading-relaxed mb-3">
              We follow industry-standard practices to safeguard any data that passes through our website. Our hosting infrastructure includes firewalls, regular security updates, and monitoring to detect and prevent unauthorized access attempts.
            </p>
            <p className="text-foreground-700 leading-relaxed">
              However, please remember that no method of electronic storage or transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security beyond what is commercially reasonable.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Third-Party Links</h2>
            <p className="text-foreground-700 leading-relaxed mb-3">
              This website contains links to external websites, including kaspreadsheet.com. When you click on an external link, you will be directed to a third-party site that is not operated by us. We strongly advise you to review the privacy policy and terms of service of every site you visit.
            </p>
            <p className="text-foreground-700 leading-relaxed">
              We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">User Responsibility</h2>
            <p className="text-foreground-700 leading-relaxed mb-3">
              While we take security seriously, staying safe online is a shared responsibility. We recommend that all users follow these best practices:
            </p>
            <ul className="space-y-2 mt-3">
              {[
                'Use strong, unique passwords for all your online accounts',
                'Keep your browser and operating system up to date',
                'Be cautious when downloading files or clicking links from unknown sources',
                'Use a reputable antivirus and firewall solution',
                'Regularly review and adjust your privacy settings on all platforms',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                  <i className="ri-shield-check-line text-primary-500 mt-0.5 shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Safe Browsing Practices</h2>
            <p className="text-foreground-700 leading-relaxed mb-3">
              When browsing litbuyspreadsheets.one or any website, we recommend the following safe browsing habits:
            </p>
            <ul className="space-y-2 mt-3">
              {[
                'Verify the URL in your address bar matches litbuyspreadsheets.one',
                'Check for the HTTPS padlock before entering any information',
                'Do not share sensitive personal information through unsecured channels',
                'Log out of shared devices after use',
                'Clear your browser cache and cookies periodically',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Contact Us About Safety</h2>
            <p className="text-foreground-700 leading-relaxed mb-3">
              If you have any questions or concerns about the safety and security of this website, or if you discover a potential security vulnerability, please contact us immediately through our main website.
            </p>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit kaspreadsheet.com
              <i className="ri-arrow-right-line"></i>
            </a>
          </section>

          <div className="mt-8 pt-6 border-t border-background-200/70">
            <p className="text-xs text-foreground-500">
              Last updated: June 2026. This safety page is reviewed regularly to reflect the latest security practices and platform updates.
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}